import {
  SubscriptionDTO,
  SubscriptionFeatureDTO,
  SubscriptionPriceDTO,
  SubscriptionProductDTO,
  SubscriptionProductPriceDTO,
  SubscriptionProductFeatureDTO,
  UsageLimitsDTO,
  CreateSubscriptionRequestDTO,
  UpdateSubscriptionRequestDTO,
  ReportUsageRequestDTO,
  CreateCheckoutSessionRequestDTO,
  CheckoutSessionDataDTO,
  PaymentMethodDTO,
} from '../dto/SubscriptionDTO';
import {
  Subscription,
  SubscriptionPlan,
  UsageLimits,
  CreateSubscriptionRequest,
  UpdateSubscriptionRequest,
  ReportUsageRequest,
  SubscriptionProductFeature,
  PaymentMethod,
} from '../../../domain/subscription/models/SubscriptionPlan';
import { CreateCheckoutSessionRequest, CheckoutSessionData } from '../../../domain/subscription/ISubscriptionRepository';

export class SubscriptionMapper {
  static toDomainSubscription(dto: SubscriptionDTO, product?: SubscriptionProductDTO): Subscription {
    // Use product from DTO if available, otherwise use the passed product parameter
    const productData = dto.product || product;

    // Extract product ID from the product data or first price if available
    const productId = productData?.id || dto.prices?.[0]?.productPrice?.productId;

    // Build the plan from product data or subscription features
    const plan = productData ? this.toDomainSubscriptionPlan(productData) : this.buildPlanFromSubscriptionFeatures(dto);

    return {
      id: dto.id,
      companyId: dto.companyId,
      planId: productId || '', // For backward compatibility
      plan: plan,
      status: dto.status as Subscription['status'],
      provider: dto.provider as Subscription['provider'],
      stripeSubscriptionId: dto.stripeSubscriptionId,
      stripeCustomerId: dto.stripeCustomerId,
      shopifyShopDomain: dto.shopifyDetail?.shopDomain,
      startDate: new Date(dto.startDate),
      endDate: dto.endDate ? new Date(dto.endDate) : undefined,
      trialEndDate: dto.trialEndDate ? new Date(dto.trialEndDate) : undefined,
      currentPeriodStart: dto.currentPeriodStart ? new Date(dto.currentPeriodStart) : undefined,
      currentPeriodEnd: dto.currentPeriodEnd ? new Date(dto.currentPeriodEnd) : undefined,
      cancelAtPeriodEnd: dto.cancelAtPeriodEnd,
      canceledAt: dto.canceledAt ? new Date(dto.canceledAt) : undefined,
      currentPeriodOrderCount: 0, // This should be calculated from usage data
      createdAt: new Date(dto.createdAt),
      updatedAt: new Date(dto.updatedAt),
      // New fields from the updated structure
      productId: productId,
      product: productData ? this.toDomainSubscriptionProduct(productData) : undefined,
      features: dto.features?.map(f => this.toDomainSubscriptionFeature(f)),
      prices: dto.prices?.map(p => this.toDomainSubscriptionPrice(p)),
    };
  }

  static toDomainSubscriptionPlan(dto: SubscriptionProductDTO): SubscriptionPlan {
    // Find the base price (usually the first price or the one with sortOrder 1)
    const basePrice = dto.prices?.find(p => p.sortOrder === 1) || dto.prices?.[0];

    // Find usage-based pricing (if any)
    const usagePrice = dto.prices?.find(p => p.pricingModel === 'usage' || p.pricingModel === 'per_unit');

    return {
      id: dto.id,
      name: dto.name,
      type: dto.type as SubscriptionPlan['type'],
      description: dto.description,
      baseFeeInCents: basePrice?.amountInCents || 0,
      feePerOrderInCents: usagePrice?.amountInCents || 0,
      trialDurationInDays: dto.trialDurationInDays,
      stripePriceId: basePrice?.stripePriceId,
      isActive: dto.isActive,
      createdAt: new Date(dto.createdAt),
      updatedAt: new Date(dto.updatedAt),
      features: dto.features?.map(f => this.toDomainSubscriptionProductFeature(f)),
    };
  }

  static toDomainUsageLimits(dto: UsageLimitsDTO): UsageLimits {
    return {
      maxProducts: dto.maxProducts,
      currentProducts: dto.currentProducts,
      maxOrdersPerMonth: dto.maxOrdersPerMonth,
      currentOrdersThisMonth: dto.currentOrdersThisMonth,
      canCreatePurchaseOrders: dto.canCreatePurchaseOrders,
      productLimitReached: dto.productLimitReached,
      orderLimitReached: dto.orderLimitReached,
    };
  }

  static toDomainCreateSubscriptionRequest(dto: CreateSubscriptionRequestDTO): CreateSubscriptionRequest {
    return {
      planId: dto.productId, // Map productId to planId for backward compatibility
      paymentMethodId: dto.paymentMethodId,
    };
  }

  static toDomainUpdateSubscriptionRequest(dto: UpdateSubscriptionRequestDTO): UpdateSubscriptionRequest {
    return {
      planId: dto.productId, // Map productId to planId for backward compatibility
      cancelAtPeriodEnd: dto.cancelAtPeriodEnd,
    };
  }

  static toDomainReportUsageRequest(dto: ReportUsageRequestDTO): ReportUsageRequest {
    return {
      companyId: dto.companyId,
      orderCount: dto.orderCount,
    };
  }

  static toDomainCreateCheckoutSessionRequest(dto: CreateCheckoutSessionRequestDTO): CreateCheckoutSessionRequest {
    return {
      planId: dto.productId, // Map productId to planId for backward compatibility
      successUrl: dto.successUrl,
      cancelUrl: dto.cancelUrl,
      mode: dto.mode,
      customerEmail: dto.customerEmail,
      clientReferenceId: dto.clientReferenceId,
    };
  }

  static toDomainCheckoutSessionData(dto: CheckoutSessionDataDTO): CheckoutSessionData {
    return {
      id: dto.id,
      url: dto.url,
      expiresAt: dto.expiresAt,
      paymentStatus: dto.paymentStatus || 'unknown',
    };
  }

  // Helper method to build plan from subscription features when product data is not available
  private static buildPlanFromSubscriptionFeatures(dto: SubscriptionDTO): SubscriptionPlan {
    // Extract pricing from subscription prices
    const basePrice = dto.prices?.find(p => p.productPrice?.pricingModel === 'flat' || p.productPrice?.sortOrder === 1);
    const usagePrice = dto.prices?.find(p => p.productPrice?.pricingModel === 'usage' || p.productPrice?.pricingModel === 'per_unit');

    return {
      id: dto.prices?.[0]?.productPrice?.productId || '',
      name: 'Subscription Plan', // Default name when product data is not available
      type: 'lite', // Default type
      description: 'Subscription plan',
      baseFeeInCents: basePrice?.productPrice?.amountInCents || 0,
      feePerOrderInCents: usagePrice?.productPrice?.amountInCents || 0,
      trialDurationInDays: 0,
      stripePriceId: basePrice?.productPrice?.stripePriceId,
      isActive: true,
      createdAt: new Date(dto.createdAt),
      updatedAt: new Date(dto.updatedAt),
    };
  }

  // Map subscription feature DTO to domain model
  static toDomainSubscriptionFeature(dto: SubscriptionFeatureDTO) {
    return {
      id: dto.id,
      subscriptionId: dto.subscriptionId,
      featureKey: dto.featureKey,
      isEnabled: dto.isEnabled,
      limitValue: dto.limitValue,
      createdAt: new Date(dto.createdAt),
      updatedAt: new Date(dto.updatedAt),
    };
  }

  // Map subscription price DTO to domain model
  static toDomainSubscriptionPrice(dto: SubscriptionPriceDTO) {
    return {
      id: dto.id,
      subscriptionId: dto.subscriptionId,
      subscriptionProductPriceId: dto.subscriptionProductPriceId,
      createdAt: new Date(dto.createdAt),
      updatedAt: new Date(dto.updatedAt),
      productPrice: dto.productPrice ? this.toDomainSubscriptionProductPrice(dto.productPrice) : undefined,
    };
  }

  // Map subscription product price DTO to domain model
  static toDomainSubscriptionProductPrice(dto: SubscriptionProductPriceDTO) {
    return {
      id: dto.id,
      productId: dto.productId,
      pricingModel: dto.pricingModel as 'flat' | 'usage' | 'per_unit' | 'tiered',
      invoicingPeriod: dto.invoicingPeriod as 'monthly' | 'yearly' | 'weekly' | 'daily',
      amountInCents: dto.amountInCents,
      currency: dto.currency,
      description: dto.description,
      stripePriceId: dto.stripePriceId,
      stripeMeterId: dto.stripeMeterId,
      sortOrder: dto.sortOrder,
      isActive: dto.isActive,
      createdAt: new Date(dto.createdAt),
      updatedAt: new Date(dto.updatedAt),
    };
  }

  // Map subscription product DTO to domain model
  static toDomainSubscriptionProduct(dto: SubscriptionProductDTO) {
    return {
      id: dto.id,
      name: dto.name,
      type: dto.type as 'free' | 'trial' | 'lite' | 'boost' | 'enterprise',
      description: dto.description,
      stripeProductId: dto.stripeProductId,
      trialDurationInDays: dto.trialDurationInDays,
      isActive: dto.isActive,
      createdAt: new Date(dto.createdAt),
      updatedAt: new Date(dto.updatedAt),
      prices: dto.prices?.map(p => this.toDomainSubscriptionProductPrice(p)),
      features: dto.features?.map(f => this.toDomainSubscriptionProductFeature(f)),
    };
  }

  // Map subscription product feature DTO to domain model
  static toDomainSubscriptionProductFeature(dto: SubscriptionProductFeatureDTO) {
    return {
      id: dto.id,
      name: dto.name,
      productId: dto.productId,
      featureKey: dto.featureKey,
      isEnabled: dto.isEnabled,
      limitValue: dto.limitValue,
      sortOrder: dto.sortOrder,
      createdAt: new Date(dto.createdAt),
      updatedAt: new Date(dto.updatedAt),
    };
  }

  // Reverse mappers (Domain to DTO)
  static toDTOCreateSubscriptionRequest(domain: CreateSubscriptionRequest): CreateSubscriptionRequestDTO {
    return {
      productId: domain.planId, // Map planId to productId
      priceId: '', // This should be provided by the service layer
      paymentMethodId: domain.paymentMethodId,
    };
  }

  static toDTOUpdateSubscriptionRequest(domain: UpdateSubscriptionRequest): UpdateSubscriptionRequestDTO {
    return {
      productId: domain.planId, // Map planId to productId
      priceId: undefined, // PriceId should be determined by the service layer
      cancelAtPeriodEnd: domain.cancelAtPeriodEnd,
    };
  }

  static toDTOReportUsageRequest(domain: ReportUsageRequest): ReportUsageRequestDTO {
    return {
      companyId: domain.companyId,
      orderCount: domain.orderCount,
    };
  }

  static toDTOCreateCheckoutSessionRequest(domain: CreateCheckoutSessionRequest): CreateCheckoutSessionRequestDTO {
    return {
      productId: domain.planId, // Map planId to productId
      priceId: '', // This should be provided by the service layer
      successUrl: domain.successUrl,
      cancelUrl: domain.cancelUrl,
      mode: domain.mode,
      customerEmail: domain.customerEmail,
      clientReferenceId: domain.clientReferenceId,
    };
  }

  // Reverse mappers for new domain models
  static toDTOSubscription(domain: Subscription): SubscriptionDTO {
    return {
      id: domain.id,
      companyId: domain.companyId,
      status: domain.status,
      stripeSubscriptionId: domain.stripeSubscriptionId,
      stripeCustomerId: domain.stripeCustomerId,
      startDate: domain.startDate.toISOString(),
      endDate: domain.endDate?.toISOString(),
      trialEndDate: domain.trialEndDate?.toISOString(),
      currentPeriodStart: domain.currentPeriodStart?.toISOString(),
      currentPeriodEnd: domain.currentPeriodEnd?.toISOString(),
      cancelAtPeriodEnd: domain.cancelAtPeriodEnd,
      canceledAt: domain.canceledAt?.toISOString(),
      createdAt: domain.createdAt.toISOString(),
      updatedAt: domain.updatedAt.toISOString(),
      features: domain.features?.map(f => this.toDTOSubscriptionFeature(f)),
      prices: domain.prices?.map(p => this.toDTOSubscriptionPrice(p)),
      product: domain.product ? this.toDTOSubscriptionProduct(domain.product) : undefined,
    };
  }

  static toDTOSubscriptionFeature(domain: any): SubscriptionFeatureDTO {
    return {
      id: domain.id,
      subscriptionId: domain.subscriptionId,
      name: domain.name,
      featureKey: domain.featureKey,
      isEnabled: domain.isEnabled,
      limitValue: domain.limitValue,
      createdAt: domain.createdAt.toISOString(),
      updatedAt: domain.updatedAt.toISOString(),
    };
  }

  static toDTOSubscriptionPrice(domain: any): SubscriptionPriceDTO {
    return {
      id: domain.id,
      subscriptionId: domain.subscriptionId,
      subscriptionProductPriceId: domain.subscriptionProductPriceId,
      createdAt: domain.createdAt.toISOString(),
      updatedAt: domain.updatedAt.toISOString(),
      productPrice: domain.productPrice ? this.toDTOSubscriptionProductPrice(domain.productPrice) : undefined,
    };
  }

  static toDTOSubscriptionProduct(domain: any): SubscriptionProductDTO {
    return {
      id: domain.id,
      name: domain.name,
      type: domain.type,
      description: domain.description,
      stripeProductId: domain.stripeProductId,
      trialDurationInDays: domain.trialDurationInDays,
      isActive: domain.isActive,
      createdAt: domain.createdAt.toISOString(),
      updatedAt: domain.updatedAt.toISOString(),
      prices: domain.prices?.map((p: any) => this.toDTOSubscriptionProductPrice(p)),
      features: domain.features?.map((f: any) => this.toDTOSubscriptionProductFeature(f)),
    };
  }

  static toDTOSubscriptionProductPrice(domain: any): SubscriptionProductPriceDTO {
    return {
      id: domain.id,
      productId: domain.productId,
      pricingModel: domain.pricingModel,
      invoicingPeriod: domain.invoicingPeriod,
      amountInCents: domain.amountInCents,
      currency: domain.currency,
      description: domain.description,
      stripePriceId: domain.stripePriceId,
      stripeMeterId: domain.stripeMeterId,
      sortOrder: domain.sortOrder,
      isActive: domain.isActive,
      createdAt: domain.createdAt.toISOString(),
      updatedAt: domain.updatedAt.toISOString(),
    };
  }

  static toDTOSubscriptionProductFeature(domain: any): SubscriptionProductFeatureDTO {
    return {
      id: domain.id,
      name: domain.name,
      productId: domain.productId,
      featureKey: domain.featureKey,
      isEnabled: domain.isEnabled,
      limitValue: domain.limitValue,
      sortOrder: domain.sortOrder,
      createdAt: domain.createdAt.toISOString(),
      updatedAt: domain.updatedAt.toISOString(),
    };
  }

  static toDomainPaymentMethod(dto: PaymentMethodDTO): PaymentMethod {
    return {
      id: dto.id,
      type: dto.type,
      card: dto.card ?
        {
          brand: dto.card.brand,
          country: dto.card.country,
          expMonth: dto.card.exp_month,
          expYear: dto.card.exp_year,
          funding: dto.card.funding,
          last4: dto.card.last4,
          fingerprint: dto.card.fingerprint,
        } : undefined,
      billingDetails: {
        address: {
          city: dto.billing_details.address.city,
          country: dto.billing_details.address.country,
          line1: dto.billing_details.address.line1,
          line2: dto.billing_details.address.line2,
          postalCode: dto.billing_details.address.postal_code,
          state: dto.billing_details.address.state,
        },
        email: dto.billing_details.email,
        name: dto.billing_details.name,
        phone: dto.billing_details.phone,
      },
      isDefault: dto.isDefault,
      created: dto.created,
    };
  }

}
